import { DialogModule } from 'primeng/dialog';
import { ImageCropperComponent } from 'ngx-image-cropper';
import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import Swal from 'sweetalert2';
import { getDocument, GlobalWorkerOptions, version } from 'pdfjs-dist';

// Configurar el worker de PDF.js
GlobalWorkerOptions.workerSrc = 'assets/pdf.worker.min.js';

// Verificar la versión del worker
console.log('PDF.js version:', version);

@Component({
  selector: 'app-recorte-foto',
  templateUrl: './recorte-foto.component.html',
  styleUrls: ['./recorte-foto.component.scss'],
  standalone: true,
  imports: [CommonModule, DialogModule, ImageCropperComponent]
})
export class RecorteFotoComponent implements OnChanges {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  private _imageUrl: string | undefined;
  @Input() set imageUrl(value: string | undefined) {
    if (value !== this._imageUrl) {
      console.log('imagen : ', value);

      if (value && (value.toLowerCase().includes('application/pdf') || value.toLowerCase().includes('.pdf'))) {
        this.convertPdfToImage(value).then(imageBase64 => {
          this._imageUrl = imageBase64;
          this.validateImage();
        }).catch(error => {
          console.error('Error completo:', error);
          this.showError('Error al convertir el PDF a imagen: ' + error.message);
        });
      } else {
        this._imageUrl = value;
        this.validateImage();
      }
    }
  }
  get imageUrl(): string | undefined {
    return this._imageUrl;
  }

  @Output() fotoRecortada = new EventEmitter<string>();
  @Output() cerrado = new EventEmitter<void>();

  base64Image: string | null = null;
  shouldRender = false;

  private async convertPdfToImage(pdfUrl: string): Promise<string> {
    try {
      // Cargar el PDF
      const loadingTask = getDocument(pdfUrl);
      const pdf = await loadingTask.promise;

      // Obtener la primera página
      const page = await pdf.getPage(1);

      // Configurar el viewport con una escala más alta para mejor calidad
      const viewport = page.getViewport({ scale: 2.0 });

      // Crear un canvas
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('No se pudo crear el contexto del canvas');
      }

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Renderizar la página en el canvas
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;

      // Convertir el canvas a base64 con alta calidad
      return canvas.toDataURL('image/jpeg', 0.95);
    } catch (error) {
      console.error('Error al convertir PDF:', error);
      throw error;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      this.shouldRender = this.visible;
    }
  }

  private validateImage(): void {
    if (!this._imageUrl) {
      this.showError('No se encontró la fotografía.');
      return;
    }
    console.log('this._imageUrl : ', this._imageUrl);
  }

  private showError(message: string): void {
    Swal.fire('Error', message, 'error');
    this.closeDialog();
  }

  private closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.cerrado.emit();
  }

  cropperReady(event: any) {
    console.log('Cropper listo:', event);
  }

  imageCropped(event: ImageCroppedEvent) {
    this.base64Image = event.base64 || null;
  }

  confirmar() {
    if (this.base64Image) {
      this.comprimirHasta3MB(this.base64Image).then((base64Final) => {
        const soloBase64 = base64Final.startsWith("data:image")
          ? base64Final.substring(base64Final.indexOf(",") + 1)
          : base64Final;
        this.fotoRecortada.emit(soloBase64);
        this.closeDialog();
      });
    } else {
      this.closeDialog();
    }
  }

  comprimirHasta3MB(base64: string, calidadInicial: number = 0.7): Promise<string> {
    const MAX_MB = 3;
    const MIN_QUALITY = 0.1;
    let calidad = calidadInicial;

    const getSizeMB = (b64: string) => {
      const str = b64.startsWith("data:image") ? b64.split(',')[1] : b64;
      return (str.length * 3) / 4 / (1024 * 1024);
    };

    return new Promise((resolve) => {
      const intentar = (b64: string, calidadActual: number) => {
        const size = getSizeMB(b64);
        if (size <= MAX_MB || calidadActual <= MIN_QUALITY) {
          resolve(b64);
          return;
        }
        this.comprimirBase64(b64, calidadActual - 0.1).then((nuevoB64) => {
          intentar(nuevoB64, calidadActual - 0.1);
        });
      };
      intentar(base64, calidad);
    });
  }

  comprimirBase64(base64: string, calidad: number = 0.7): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const base64Comprimido = canvas.toDataURL('image/jpeg', calidad);
          resolve(base64Comprimido);
        } else {
          resolve(base64);
        }
      };
      img.onerror = () => resolve(base64);
      img.src = base64;
    });
  }

  cancelar() {
    this.closeDialog();
  }
}
